import { computed } from 'vue';
import { useRoute, useRouter, Router } from 'vue-router';
import globalRouter from "@/router";
import { AuthService } from '@/services/AuthService';
import { StorageService } from "@/services/storageService.ts";
import { useServiceStore } from '@/stores/useServiceStore';
import { useSettingsStore } from '@/stores/useSettingsStore';
import { getIframeBaseUrl, isNative } from "@/utils/platform.ts";
import {useServices} from "@/composables/useServices.ts";
import {generateHandshakeNonce} from "@/utils/security/messaging.ts";

export function useNavigation(customRouter?: Router) {
    // Fallback gracefully if useRouter() returns undefined
    const router = customRouter || useRouter() || globalRouter;

    // Safety check for useRoute in non-component contexts
    let route: any;
    try {
        route = useRoute();
    } catch (e) {
        route = router?.currentRoute.value;
    }

    const serviceStore = useServiceStore();
    const settingsStore = useSettingsStore();
    const { visitService } = useServices();

    const isRootPath = computed(() => {
        return !serviceStore.selected || !serviceStore.selected.id || serviceStore.selected.isRoot
    });

    const isLoginPage = computed(() => route?.path === '/login');
    const currentPath = computed(() => route?.path || '');

    const buildServiceUrl = async (serviceRelativeUrl: string, serviceId?: string): Promise<string> => {
        if (!serviceRelativeUrl) return 'about:blank';

        const baseUrl = await getIframeBaseUrl();
        const finalUrl = new URL(baseUrl + serviceRelativeUrl);

        const historyState = window.history.state;
        let activeToken = historyState?.token;
        if (!activeToken) {
            activeToken = await StorageService.Get("ACCESS_TOKEN");
        }

        const session = await AuthService.findSession().catch(() => null);
        const forceUrlToken = session?.source === 'url' || historyState?.tokenSource === 'url';

        if (activeToken && (isNative || forceUrlToken)) {
            finalUrl.searchParams.set('token', activeToken);
        }
        finalUrl.searchParams.set('origin', window.location.origin);
        if (serviceId) {
            try {
                const nonce = await generateHandshakeNonce(serviceId);
                finalUrl.searchParams.set('nonce', nonce);
                console.log("Appended security params to service URL:", finalUrl);
            } catch (e) {
                console.error("Failed to append security params to service URL", e);
            }
        }
        return finalUrl.toString();
    };

    const goTo = async (path: string, options: { state?: any; replace?: boolean } = {}) => {
        if (!path) return;

        if (path === '/' || path === '') {
            serviceStore.clearSelection();
        }

        const potentialServiceId = path.startsWith('/') ? path.slice(1) : path;
        if (settingsStore.services?.[potentialServiceId]) {
            serviceStore.selectService(settingsStore.services[potentialServiceId]);
            visitService(potentialServiceId)
        }

        const isInternal = path.startsWith('/') || (!path.includes('.') && !path.startsWith('http'));

        if (isInternal) {
            let activeToken = options.state?.token;
            if (!activeToken) {
                const session = await AuthService.findSession().catch(() => null);
                if (session && session.source === 'url') {
                    activeToken = session?.token;
                }
            }

            const cleanPath = path.startsWith('/') ? path : `/${path}`;

            const routerState = {
                ...options.state,
                ...(activeToken ? { token: activeToken } : {})
            };

            // This will now reliably use whichever router instance is active
            if (options.replace) {
                await router.replace({ path: cleanPath, state: routerState });
            } else {
                await router.push({ path: cleanPath, state: routerState });
            }

        } else {
            // External Redirect
            const externalUrl = path.startsWith('http') ? path : `https://${path}`;
            window.location.href = externalUrl;
        }
    };

    const goBack = () => router.back();

    const getQueryParam = (key: string) => {
        if (window.history.state && window.history.state[key]) {
            return window.history.state[key];
        }
        return route?.query?.[key];
    };

    return {
        isRootPath,
        isLoginPage,
        currentPath,
        buildServiceUrl,
        goTo,
        goBack,
        getQueryParam
    };
}